import { PieceMediaCard } from './PieceMediaCard';
import { FollowUpCard } from './FollowUp';
import { useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { Channel, ContentPieceDetail, ContentRevision } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { bibleLink } from '../bible/BiblePage';
import { CHANNEL, EVENT, KIND, paragraphs, STATUS } from './labels';
import { PieceForm } from './PieceForm';
import { PublishPanel } from './PublishPanel';

const when = (iso: string) => new Date(iso).toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/** Crear una pieza nueva. */
export function NewPiecePage() {
  const me = useMe();
  const navigate = useNavigate();
  if (!me.activeChurchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;
  return (
    <div className="page page--narrow">
      <p className="small">
        <Link to="/app/contenido">← Contenido</Link>
      </p>
      <header className="page__header">
        <h1>Nueva predicación o estudio</h1>
      </header>
      <Card>
        <PieceForm churchId={me.activeChurchId} onSaved={(p) => navigate(`/app/contenido/${p.id}`, { replace: true })} />
      </Card>
    </div>
  );
}

/** Una pieza: la versión actual, su flujo de revisión, su historia y dónde está publicada. */
export function PiecePage() {
  const { pieceId = '' } = useParams();
  const me = useMe();
  const churchId = me.activeChurchId;
  const [version, setVersion] = useState(0);
  const [editing, setEditing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const piece = useAsync(
    (signal) => (churchId ? api.get<ContentPieceDetail>(`/content/churches/${churchId}/pieces/${pieceId}`, signal) : Promise.reject(new Error('Sin contexto'))),
    [churchId, pieceId, version],
  );
  const last = useRef<ContentPieceDetail | null>(null);
  if (piece.status === 'ready') last.current = piece.data;
  const p = piece.status === 'ready' ? piece.data : last.current;
  const changed = (message: string) => {
    setNotice(message);
    setEditing(false);
    setVersion((v) => v + 1);
  };

  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;

  return (
    <div className="page page--narrow">
      <p className="small">
        <Link to="/app/contenido">← Contenido</Link>
      </p>
      {piece.status === 'loading' && !p ? <Loading /> : null}
      {piece.status === 'error' ? <ErrorState error={piece.error} onRetry={piece.reload} /> : null}
      {p ? (
        <>
          <header className="page__header">
            <p className="muted small">
              {KIND[p.kind]} · versión {p.currentRevision} <span className={`badge ${STATUS[p.status].tone}`}>{STATUS[p.status].label}</span>
            </p>
            <h1>{p.revision.title}</h1>
          </header>
          {notice ? <Alert tone="success">{notice}</Alert> : null}
          <ReturnedNote piece={p} />
          {editing ? (
            <Card title={`Editar (versión ${p.currentRevision + 1})`}>
              <PieceForm churchId={churchId} piece={p} onSaved={() => changed('Versión guardada.')} onCancel={() => setEditing(false)} />
            </Card>
          ) : (
            <>
              <Workflow churchId={churchId} piece={p} onEdit={() => setEditing(true)} onChange={changed} />
              <Card>
                <Reading revision={p.revision} linkPassage />
              </Card>
            </>
          )}
          <PieceMediaCard churchId={churchId} pieceId={p.id} canEdit={me.can('content.write')} />
          <FollowUpCard churchId={churchId} pieceId={p.id} canEdit={me.can('content.write')} />
          {me.can('publishing.manage') ? <PublishPanel churchId={churchId} piece={p} version={version} /> : null}
          <Card title="Cambios">
            <ul className="list">
              {[...p.events].reverse().map((e, i) => (
                <li key={i} className="list__item">
                  <span>
                    <strong>{EVENT[e.action]}</strong> <span className="muted small">v{e.revision} · {e.actor.name} · {when(e.at)}</span>
                  </span>
                  {e.note ? <span className="small">“{e.note}”</span> : null}
                </li>
              ))}
            </ul>
          </Card>
        </>
      ) : null}
    </div>
  );
}

/** El texto de una versión, como lo leerá la iglesia. */
export function Reading({ revision: r, linkPassage = false }: { revision: Pick<ContentRevision, 'summary' | 'body' | 'preacher' | 'passage' | 'preachedOn'>; linkPassage?: boolean }) {
  const passage = r.passage && linkPassage ? <Link to={bibleLink(r.passage)}>{r.passage}</Link> : r.passage;
  const meta = [r.preacher, passage, r.preachedOn ? new Date(`${r.preachedOn}T12:00:00`).toLocaleDateString('es', { day: 'numeric', month: 'long', year: 'numeric' }) : null].filter(Boolean);
  return (
    <article className="reading">
      {meta.length > 0 ? (
        <p className="reading__meta">
          {meta.map((m, i) => (
            <span key={i}>
              {i > 0 ? ' · ' : ''}
              {m}
            </span>
          ))}
        </p>
      ) : null}
      {r.summary ? (
        <p>
          <em>{r.summary}</em>
        </p>
      ) : null}
      {paragraphs(r.body).map((text, i) => (
        <p key={i}>{text}</p>
      ))}
    </article>
  );
}

function ReturnedNote({ piece: p }: { piece: ContentPieceDetail }) {
  const lastReview = [...p.events].reverse().find((e) => e.action === 'returned' || e.action === 'approved' || e.action === 'submitted');
  if (p.status !== 'draft' || lastReview?.action !== 'returned') return null;
  return (
    <Alert tone="info">
      {lastReview.actor.name} pidió cambios: {lastReview.note}
    </Alert>
  );
}

/** Pasos posibles según el estado y los permisos (el backend vuelve a decidir). */
function Workflow({ churchId, piece: p, onEdit, onChange }: { churchId: string; piece: ContentPieceDetail; onEdit: () => void; onChange: (m: string) => void }) {
  const me = useMe();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [returning, setReturning] = useState(false);
  const canWrite = me.can('content.write');
  const canApprove = me.can('content.approve');
  // Quien escribe, aprueba y publica lo hace en un solo paso (#66); quien solo escribe envía a revisión.
  const oneStep = canWrite && canApprove && me.can('publishing.manage');
  const [publishing, setPublishing] = useState(false);
  const [channels, setChannels] = useState<Set<Channel>>(new Set(['church']));

  async function approveAndPublish() {
    setBusy('approve-publish');
    setError(null);
    const base = `/content/churches/${churchId}/pieces/${p.id}`;
    try {
      if (p.status === 'draft') await api.post(`${base}/submit`);
      await api.post(`${base}/review`, { decision: 'approve' });
      await api.post(`/publishing/churches/${churchId}/publications`, { pieceId: p.id, channels: [...channels], publishAt: null });
      setPublishing(false);
      onChange(`Aprobada y publicada en ${[...channels].map((c) => CHANNEL[c]).join(' y ')}.`);
    } catch (e) {
      setError(errorMessage(e));
      // Lo que alcanzó a hacerse (enviar o aprobar) ya quedó: se vuelve a leer la pieza.
      onChange('');
    } finally {
      setBusy(null);
    }
  }

  async function run(action: string, message: string, body?: unknown) {
    setBusy(action);
    setError(null);
    try {
      await api.post(`/content/churches/${churchId}/pieces/${p.id}/${action}`, body);
      setReturning(false);
      onChange(message);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  function submitReturn(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void run('review', 'Devuelta con cambios pedidos.', { decision: 'return', note: String(new FormData(event.currentTarget).get('note') ?? '') });
  }

  const buttons = [
    p.status === 'draft' && canWrite && !oneStep ? (
      <Button key="submit" busy={busy === 'submit'} onClick={() => run('submit', 'Enviada a revisión.')}>
        Enviar a revisión
      </Button>
    ) : null,
    (p.status === 'draft' || p.status === 'in_review') && oneStep ? (
      <Button key="approve-publish" aria-expanded={publishing} onClick={() => setPublishing((x) => !x)}>
        Aprobar y publicar
      </Button>
    ) : null,
    p.status === 'in_review' && canApprove ? (
      <Button key="approve" busy={busy === 'review' && !returning} onClick={() => run('review', 'Aprobada. Ya se puede publicar.', { decision: 'approve' })}>
        Aprobar
      </Button>
    ) : null,
    p.status === 'in_review' && canApprove ? (
      <Button key="return" variant="secondary" onClick={() => setReturning((r) => !r)}>
        Pedir cambios
      </Button>
    ) : null,
    p.status !== 'archived' && canWrite ? (
      <Button key="edit" variant="ghost" onClick={onEdit}>
        Editar
      </Button>
    ) : null,
    p.status !== 'archived' && canWrite ? (
      <Button key="archive" variant="ghost" busy={busy === 'archive'} onClick={() => run('archive', 'Pieza archivada.')}>
        Archivar
      </Button>
    ) : null,
    p.status === 'archived' && canWrite ? (
      <Button key="restore" variant="secondary" busy={busy === 'restore'} onClick={() => run('restore', 'Pieza restaurada como borrador.')}>
        Restaurar
      </Button>
    ) : null,
  ].filter(Boolean);

  if (buttons.length === 0) return null;
  return (
    <>
      <div className="actions actions--wrap">{buttons}</div>
      {publishing ? (
        <Card title="Aprobar y publicar">
          <fieldset className="choices choices--row">
            <legend>¿Dónde se publica?</legend>
            {(Object.keys(CHANNEL) as Channel[]).map((c) => (
              <label key={c} className="choice">
                <input type="checkbox" checked={channels.has(c)} onChange={() => setChannels((s) => (s.has(c) ? new Set([...s].filter((x) => x !== c)) : new Set([...s, c])))} />
                <span>{CHANNEL[c]}</span>
              </label>
            ))}
          </fieldset>
          <div className="actions">
            <Button busy={busy === 'approve-publish'} disabled={channels.size === 0} onClick={() => void approveAndPublish()}>
              Publicar ahora
            </Button>
            <Button variant="ghost" onClick={() => setPublishing(false)}>
              Cancelar
            </Button>
          </div>
        </Card>
      ) : null}
      {returning ? (
        <Card title="Pedir cambios">
          <form className="form" onSubmit={submitReturn} noValidate>
            <Field label="Qué hay que cambiar" name="note" required maxLength={500} />
            <Button type="submit" variant="secondary" busy={busy === 'review'}>
              Devolver
            </Button>
          </form>
        </Card>
      ) : null}
      {error ? <Alert>{error}</Alert> : null}
    </>
  );
}
